// =====================================================
// AUDIT EXPORT MODAL - PDF Report Generation
// Institution-grade compliance reporting
// =====================================================

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import { FiX, FiDownload, FiFileText, FiCalendar, FiLoader } from 'react-icons/fi';
import { generateAuditReport } from '@/services/auditExportService';
import type { DateRangePreset } from '@/types/auditReport';

interface AuditExportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AuditExportModal: React.FC<AuditExportModalProps> = ({ isOpen, onClose }) => {
  const [selectedRange, setSelectedRange] = useState<DateRangePreset>('30');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dateRanges: { value: DateRangePreset; label: string; description: string }[] = [
    { value: '7', label: '7 Days', description: 'Last week of trading activity' },
    { value: '30', label: '30 Days', description: 'Last month of trading activity' },
    { value: '90', label: '90 Days', description: 'Last quarter of trading activity' },
    { value: 'all', label: 'All Time', description: 'Complete trading history' },
  ];

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    
    try {
      const result = await generateAuditReport(selectedRange);
      
      if (result.success) {
        onClose();
      } else {
        setError(result.error || 'Failed to generate report');
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('[AuditModal] Error:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="relative w-full max-w-md mx-4 bg-gray-900 rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-white/5">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-purple-500/20">
                <SafeIcon icon={FiFileText} className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Export PDF Audit</h3>
                <p className="text-xs text-gray-500">Institutional compliance report</p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isGenerating}
              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-50"
            >
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-5 space-y-4">
            {/* Date Range Selection */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-3">
                <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-500" />
                Select Date Range
              </label>
              <div className="grid grid-cols-2 gap-2">
                {dateRanges.map((range) => (
                  <button
                    key={range.value}
                    onClick={() => setSelectedRange(range.value)}
                    disabled={isGenerating}
                    className={`p-3 rounded-xl border transition-all text-left ${
                      selectedRange === range.value
                        ? 'bg-purple-500/20 border-purple-500/50 text-white'
                        : 'bg-gray-800/50 border-white/5 text-gray-400 hover:border-white/10 hover:text-white'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <span className="block font-semibold text-sm">{range.label}</span>
                    <span className="block text-xs text-gray-500 mt-0.5">{range.description}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Report Contents Preview */}
            <div className="p-4 rounded-xl bg-gray-800/30 border border-white/5">
              <h4 className="text-sm font-medium text-white mb-2">Report Includes:</h4>
              <ul className="space-y-1.5 text-xs text-gray-400">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                  TDI Score & Tier with component breakdown
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                  Key KPIs: Decision Quality, Capital Preservation, Risk Avoidance
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  Counterfactual Proof with capital preserved metrics
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                  Outcome distribution by market & asset
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                  Complete decision log (appendix)
                </li>
              </ul>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-sm text-red-400">
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-5 border-t border-white/5 bg-gray-800/30">
            <button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGenerating ? (
                <>
                  <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
                  Generating PDF Audit...
                </>
              ) : (
                <>
                  <SafeIcon icon={FiDownload} className="w-5 h-5" />
                  Generate PDF Report
                </>
              )}
            </button>
            <p className="text-xs text-center text-gray-500 mt-3">
              "Anyone can take a trade. Professionals know when not to."
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AuditExportModal;
